import { Dispatch, SetStateAction, useState } from "react";
import CostColumns from "../lib/CostColumns";
import OperationLayout from "../lib/OperationLayout";
import Input from "../lib/Input";
import VolumeDiscount from "../lib/VolumeDiscount";
import useOperationContext from "../lib/useOperationContext";
import SelectNamedTimeCost from "../lib/SelectNamedTimeCost";
import OperationHeader from "../lib/OperationHeader";
import EditableCostColumns from "../lib/EditableCostColumns";
import { quantityColumnWidth } from "../constants";
import useOperationListContext from "../lib/useOperationListContext";
import { convertUnit, DurationUnit } from "@/lib/units";
import { hasSomeValidationErrorsInFields } from "../lib/getValidationErrors";
import { useReuseFieldValue } from "../lib/useReuseFieldValue";
import { getIn } from "@/lib/utils";
import { Operation } from "../lib/types";

export default function OperationPerPartTimeDependent() {
  const { operationData: data } = useOperationContext();
  const { id, validation_errors } = data;
  const expandByDefault = hasSomeValidationErrorsInFields({
    errors: validation_errors,
    expandedPanelFields: ["discount_rates", "setup_cost", "run_cost", "markup"],
  });
  const [expand, setExpand] = useState(expandByDefault);

  return (
    <OperationLayout
      id={id as string}
      left={<Left operation={data} expand={expand} setExpand={setExpand} />}
      right={<Right expand={expand} />}
    />
  );
}

function Left({
  operation,
  expand,
  setExpand,
}: {
  operation: Operation;
  expand: boolean;
  setExpand: Dispatch<SetStateAction<boolean>>;
}) {
  const {
    markedForReuse: markedForReuseSetupTime,
    toggleFieldReuse: toggleFieldReuseSetupTime,
  } = useReuseFieldValue({
    operationType: operation.type,
    operationName: operation.name,
    keyPath: ["setup_time"],
  });

  const {
    markedForReuse: markedForReuseRunTime,
    toggleFieldReuse: toggleFieldReuseRunTime,
  } = useReuseFieldValue({
    operationType: operation.type,
    operationName: operation.name,
    keyPath: ["run_time"],
  });

  return (
    <div className="w-full flex flex-col p-2 pb-1 gap-2 items-start">
      <OperationHeader
        operation={operation}
        expand={expand}
        setExpand={setExpand}
      >
        <div className="flex gap-2">
          <Input
            label="Setup Time"
            keyPath={["setup_time"]}
            unit="time"
            markedForReuse={markedForReuseSetupTime}
            toggleFieldReuse={toggleFieldReuseSetupTime}
          />
          <Input
            label="Run Time"
            keyPath={["run_time"]}
            unit="time"
            markedForReuse={markedForReuseRunTime}
            toggleFieldReuse={toggleFieldReuseRunTime}
          />
        </div>
      </OperationHeader>
      {expand && (
        <div className="w-full flex justify-between items-start">
          <VolumeDiscount />
          <div className="flex flex-col gap-2">
            <SelectNamedTimeCost label="Setup Rate" keyPath={["setup_cost"]} />
            <SelectNamedTimeCost label="Runtime Rate" keyPath={["run_cost"]} />
          </div>
          <div className="flex flex-col self-stretch">
            <div className="text-xs text-gray-400 grow mt-0.5">Total</div>
            <div className="text-xs text-gray-400 grow">Cost</div>
            <Input label="Markup" keyPath={["markup"]} suffix="%" width={60} />
          </div>
        </div>
      )}
    </div>
  );
}

function Right({ expand }: { expand: boolean }) {
  return (
    <>
      {expand && <TotalTime />}
      {expand && <EditableCostColumns />}
      <CostColumns />
    </>
  );
}

function format(value: number) {
  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: 3,
  }).format(value);
}

function TotalTime() {
  const { quantities } = useOperationListContext();
  const {
    operationData: { draft, augmented },
  } = useOperationContext();
  const setupTime = Number(
    getIn(draft, ["setup_time", "value"], null) ||
      getIn(augmented, ["setup_time", "value"], null),
  );
  const runTime = Number(
    getIn(draft, ["run_time", "value"], null) ||
      getIn(augmented, ["run_time", "value"], null),
  );

  const setupTimeUnit: DurationUnit | null =
    getIn(draft, ["setup_time", "unit"], null) ||
    getIn(augmented, ["setup_time", "unit"], null);
  const runTimeUnit: DurationUnit | null =
    getIn(draft, ["run_time", "unit"], null) ||
    getIn(augmented, ["run_time", "unit"], null);

  const noValue = runTime === 0 && setupTime === 0;

  return (
    <div className="grow flex divide-x divide-slate-100">
      {quantities.map((q) => (
        <div
          key={q}
          className="flex flex-col justify-end items-center text-xs text-gray-400"
          style={{
            maxWidth: quantityColumnWidth,
            minWidth: quantityColumnWidth,
          }}
        >
          {noValue || setupTimeUnit === null || runTimeUnit === null
            ? ""
            : format(
                convertUnit(setupTime, setupTimeUnit, "h") +
                  convertUnit(runTime, runTimeUnit, "h") * q,
              ) + " hr"}
        </div>
      ))}
    </div>
  );
}
